// @ts-check
// taskService.js
// Purpose: Handles all CRUD operations and cache logic for tasks in Supabase.
// Features: PERMANENT CACHE + INCREMENTAL SYNC for optimal performance and cost efficiency
//
// CACHING STRATEGY OVERVIEW:
// ========================
// Traditional Approach (OLD): Cache expires every 5 minutes → Full refetch of ALL tasks
// New Approach (IMPLEMENTED): Permanent cache + incremental sync for new/updated tasks only
//
// BENEFITS:
// - Instant loading: Tasks always available from cache
// - Cost efficient: Only fetch new/changed tasks since last sync
// - Bandwidth friendly: Minimal data transfer
// - Offline resilient: Works with cached data when offline
// - Real-time feel: Background sync keeps data fresh
//
// HOW IT WORKS:
// 1. First load: Full fetch → Store in permanent cache with sync timestamp
// 2. Subsequent loads: Return cached data instantly → Background incremental sync
// 3. Incremental sync: Only fetch tasks newer/updated since lastSync timestamp
// 4. New data creation: Add directly to cache (no clearing)
// 5. Fallback: Full refresh if incremental sync fails repeatedly
//
// TASKS SPECIAL CONSIDERATIONS:
// - Tasks are frequently updated (status changes, completion, etc.)
// - Incremental sync uses OR condition: created_at > lastSync OR updated_at > lastSync
// - This ensures both new tasks and status updates are captured efficiently
//
// Inputs: userId, task data, cacheManager, supabase

import { supabase } from '../../config/supabase';
import { getAppSessionId, getUuidSessionId } from '../../utils/activityEvents';
// Use centralized cacheManager to avoid duplicate instances. Prefer this path across services.
import logger from '../../utils/logger';
import { cacheManager } from '../cacheManager';
import { TaskSchema } from '../../lib/schemas/task';
// Centralized logger for consistent, level-based logging across services

// Capability detection: some tables may not have an updated_at column.
// We persist detection to avoid repeating a failing request that would log 400s.
const TASKS_UPDATED_AT_SUPPORT_KEY = 'tg_tasks_supports_updated_at';

/**
 * Detect if an error represents an intentional/benign abort.
 * Supabase surfaces aborts with code "20" and message containing "aborted".
 * @param {any} e
 */
function isAbortError(e) {
  const msg = (e && (e.message || e.details || e.hint)) || '';
  return (e && e.name === 'AbortError') || e?.code === '20' || /aborted/i.test(String(msg));
}

/**
 * Task record shape used across services and hooks.
 * NOTE: This mirrors the DB row plus UI aliases added by normalizeTask.
 * Only key fields are strictly typed; the rest are flexible to accommodate schema differences.
 * @typedef {Object} Task
 * @property {string} id
 * @property {string} [user_id]
 * @property {string | undefined} [title]
 * @property {string | undefined} [due_date]
 * @property {string | undefined} [deadline]
 * @property {boolean} [completed]
 * @property {string} [created_at]
 * @property {string} [updated_at]
 * @property {string | undefined} [deleted_at]
 * @property {string | undefined} [linked_goal_title]
 * @property {string | undefined} [linked_plan_title]
 * @property {number | undefined} [linked_goal_color_key]
 * @property {string | undefined} [start_time]
 * @property {string | undefined} [end_time]
 * @property {boolean | undefined} [is_all_day]
 * @property {string | undefined} [all_day_date]
 * @property {number | undefined} [estimated_duration_minutes]
 */

// Normalize DB row to UI-friendly shape without losing original fields
// - Maps: task -> title, deadline -> due_date, done -> completed
// - Returns a new object with both original and friendly aliases
/**
 * Normalize a task row to include UI-friendly aliases.
 * @param {Record<string, any>} row
 * @returns {Partial<Task>}
 */
function normalizeTask(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    // UI-friendly aliases
    title: row.title ?? row.task ?? undefined,
    // Date-only alias for UI filters and grouping (YYYY-MM-DD)
    due_date:
      typeof (row.due_date ?? row.deadline) === 'string'
        ? String(row.due_date ?? row.deadline).slice(0, 10)
        : (row.due_date ?? row.deadline ?? undefined),
    // Preserve full timestamp separately for time-aware operations
    deadline: row.deadline ?? row.due_date ?? undefined,
    completed:
      typeof row.completed === 'boolean'
        ? row.completed
        : typeof row.done === 'boolean'
          ? row.done
          : false,
    // Flatten related titles from joined relations if present
    // Supabase join shape examples:
    // - goals: { title }
    // - plans: { title, linked_goal_title? }
    // Prefer direct goal title from joined goal; plan title from joined plan
    linked_goal_title: (row.linked_goal_title ?? row.goal_title ?? row.goals?.title) || undefined,
    // Surface parent goal color_key if present so UI can colorize without waiting
    linked_goal_color_key:
      typeof row.linked_goal_color_key === 'number'
        ? row.linked_goal_color_key
        : typeof row.goal_color_key === 'number'
          ? row.goal_color_key
          : typeof row.goals?.color_key === 'number'
            ? /** @type {any} */ (row.goals).color_key
            : undefined,
    linked_plan_title: (row.linked_plan_title ?? row.plan_title ?? row.plans?.title) || undefined,
    // Calendar scheduling fields
    start_time: row.start_time ?? undefined,
    end_time: row.end_time ?? undefined,
    is_all_day: row.is_all_day ?? false,
    all_day_date: row.all_day_date ?? undefined,
    estimated_duration_minutes: row.estimated_duration_minutes ?? undefined,
  };
}

// Translate UI-friendly fields back to DB column names for write operations
// - Maps: title -> task, due_date -> deadline, completed -> done
// - Leaves original DB fields if already provided
/**
 * Convert UI-friendly task fields back to DB column names.
 * @param {Record<string, any>} input
 * @returns {Record<string, any>}
 */
function dbFormatTask(input) {
  if (!input || typeof input !== 'object') return input;
  const out = { ...input };
  if (
    Object.prototype.hasOwnProperty.call(out, 'title') &&
    !Object.prototype.hasOwnProperty.call(out, 'task')
  ) {
    out.task = out.title;
    delete out.title;
  }
  if (
    Object.prototype.hasOwnProperty.call(out, 'due_date') &&
    !Object.prototype.hasOwnProperty.call(out, 'deadline')
  ) {
    out.deadline = out.due_date;
    delete out.due_date;
  }
  // Handle deadline: keep full timestamp for timed tasks, date-only for all-day tasks
  if (Object.prototype.hasOwnProperty.call(out, 'deadline')) {
    const dl = /** @type {any} */ (out).deadline;
    if (dl instanceof Date) {
      // Send as ISO string (includes time) - PostgreSQL TIMESTAMPTZ will handle it
      out.deadline = dl.toISOString();
    } else if (typeof dl === 'string' && dl.length > 10) {
      // Already a datetime string, keep as-is
      out.deadline = dl;
    } else if (typeof dl === 'string') {
      // Date-only string (YYYY-MM-DD), keep as-is
      out.deadline = dl;
    }
  }
  if (
    Object.prototype.hasOwnProperty.call(out, 'completed') &&
    !Object.prototype.hasOwnProperty.call(out, 'done')
  ) {
    out.done = out.completed;
    delete out.completed;
  }
  // Handle start_time: convert Date to ISO string for Supabase
  if (Object.prototype.hasOwnProperty.call(out, 'start_time')) {
    const st = out.start_time;
    if (st instanceof Date) {
      out.start_time = st.toISOString();
    }
  }
  // Handle end_time: convert Date to ISO string for Supabase
  if (Object.prototype.hasOwnProperty.call(out, 'end_time')) {
    const et = out.end_time;
    if (et instanceof Date) {
      out.end_time = et.toISOString();
    }
  }
  // Remove internal UI-only fields that should not be sent to DB
  delete out.__addToPlanAction;
  return out;
}
function getTasksSupportsUpdatedAt() {
  try {
    const val = localStorage.getItem(TASKS_UPDATED_AT_SUPPORT_KEY);
    if (val === null) return false; // default conservative: avoid updated_at to prevent 400s
    return val === 'true';
  } catch (_) {
    return false;
  }
}
/**
 * Persist whether tasks table supports updated_at.
 * @param {boolean} value
 */
function setTasksSupportsUpdatedAt(value) {
  try {
    localStorage.setItem(TASKS_UPDATED_AT_SUPPORT_KEY, value ? 'true' : 'false');
  } catch (_) {
    // ignore storage errors
  }
}

/**
 * Get all tasks for a user with permanent cache and incremental sync
 *
 * PERMANENT CACHE STRATEGY:
 * ========================
 * This function implements a sophisticated caching strategy that eliminates the need for
 * repeated full data fetches, dramatically improving performance and reducing API costs.
 *
 * FLOW:
 * 1. Check permanent cache first - if data exists, return immediately (instant loading)
 * 2. Trigger background incremental sync to fetch only new/updated tasks since last sync
 * 3. If no cache exists, perform full fetch and establish cache baseline
 * 4. Robust fallback: return cached data even if fresh fetch fails
 *
 * INCREMENTAL SYNC LOGIC:
 * - Uses lastSync timestamp to query only tasks newer/updated than cache
 * - Merges new/updated tasks with existing cache (no duplicates)
 * - Updates sync timestamp for next incremental fetch
 * - Falls back to full refresh if incremental sync fails repeatedly
 *
 * TASKS-SPECIFIC OPTIMIZATIONS:
 * - Handles frequent status updates efficiently
 * - Captures both new tasks and task completions/changes
 * - Maintains task order and completion states accurately
 *
 * @param {string} userId - User ID
 * @param {{ forceRefresh?: boolean, signal?: AbortSignal, committedOnly?: boolean }} [options] - Options including forceRefresh and optional AbortSignal
 * @returns {Promise<Task[]>} Array of tasks
 */
export async function getTasks(
  userId,
  { forceRefresh = false, signal, committedOnly = true } = {},
) {
  if (!userId) {
    logger.warn('⚠️ taskService.getTasks: Missing userId');
    return [];
  }

  const cacheKey = 'tasks';
  logger.info('📋 taskService.getTasks: Fetching tasks for user:', userId, { forceRefresh });

  // STEP 1: PERMANENT CACHE CHECK
  // =============================
  // Prefer cached data for instant UI, but avoid the "empty baseline" trap.
  if (!forceRefresh && cacheManager.hasData(cacheKey, userId)) {
    const cached = cacheManager.get(cacheKey, userId);
    const hasBaseline = Boolean(cacheManager.getLastSyncTimestamp(cacheKey, userId));
    if (cached && Array.isArray(cached)) {
      if (cached.length > 0 && hasBaseline) {
        logger.debug('✅ taskService.getTasks: Returning cached tasks:', cached.length);
        // Normalize in case older cache entries lack aliases and filter out uncommitted
        return /** @type {Task[]} */ (
          cached
            .map(normalizeTask)
            .filter((t) =>
              committedOnly ? /** @type {any} */ (t)?.user_committed !== false : true,
            )
        );
      }

      // If cache exists but is empty or baseline missing, perform FULL FETCH now
      logger.info(
        '⚠️ taskService.getTasks: Cached tasks are empty or missing baseline. Performing full fetch. { cachedLength:',
        cached.length,
        ', hasBaseline:',
        hasBaseline,
        ' }',
      );
      try {
        const fresh = await performFullFetch(userId, cacheKey, signal, committedOnly);
        return Array.isArray(fresh) ? fresh : [];
      } catch (e) {
        if (isAbortError(e)) {
          logger.debug('⏹️ taskService.getTasks: Full fetch after empty cache aborted');
          return [];
        }
        logger.error('❌ taskService.getTasks: Full fetch after empty cache failed:', e, {
          hasBaseline,
        });
        return [];
      }
    }
  }

  // STEP 2: FULL FETCH (NO CACHE OR FORCE REFRESH)
  // ==============================================
  // No cache exists or user explicitly requested fresh data
  // Perform full fetch to establish cache baseline
  try {
    logger.info('🔄 taskService.getTasks: Performing full fetch (no cache or force refresh)');
    return await performFullFetch(userId, cacheKey, signal, committedOnly);
  } catch (error) {
    if (isAbortError(error)) {
      logger.debug('⏹️ taskService.getTasks: Full fetch aborted');
      return [];
    }
    logger.error('❌ taskService.getTasks: Full fetch failed:', error, {
      hasBaseline: Boolean(cacheManager.getLastSyncTimestamp(cacheKey, userId)),
    });

    // FALLBACK STRATEGY
    // ================
    // If fresh fetch fails, return cached data if available (even if potentially stale)
    // This ensures app remains functional even with network issues
    const fallbackCache = cacheManager.get(cacheKey, userId);
    if (fallbackCache) {
      const count = Array.isArray(fallbackCache) ? fallbackCache.length : 0;
      logger.info('🔄 taskService.getTasks: Returning fallback cached data:', count);
      return Array.isArray(fallbackCache)
        ? /** @type {Task[]} */ (
            fallbackCache
              .map(normalizeTask)
              .filter((t) =>
                committedOnly ? /** @type {any} */ (t)?.user_committed !== false : true,
              )
          )
        : [];
    }

    // No cache available, return empty array
    logger.warn('⚠️ taskService.getTasks: No cache available, returning empty array');
    return [];
  }
}

/**
 * Perform full fetch of all tasks for a user
 *
 * FULL FETCH STRATEGY:
 * ===================
 * This function is used when:
 * 1. No cache exists (first time loading tasks)
 * 2. User explicitly requests fresh data (forceRefresh=true)
 * 3. Incremental sync has failed multiple times (fallback)
 *
 * ESTABLISHES CACHE BASELINE:
 * - Fetches ALL tasks for the user
 * - Stores them in permanent cache
 * - Sets sync timestamp for future incremental syncs
 * - This becomes the foundation for all future incremental updates
 *
 * @param {string} userId - User ID
 * @param {string} cacheKey - Cache key for tasks
 * @param {(AbortSignal|undefined)} signal - Abort signal (optional)
 * @param {boolean} committedOnly - If true, only return rows where user_committed = true
 * @returns {Promise<Task[]>} Array of tasks
 */
async function performFullFetch(userId, cacheKey, signal, committedOnly) {
  logger.info('🔄 taskService.performFullFetch: Starting full fetch from Supabase');
  // FULL FETCH QUERY
  // ===============
  // Fetch ALL tasks for this user (no filtering)
  // This establishes the complete baseline for future incremental syncs
  let fullQuery = supabase
    .from('tasks')
    // Include related goal/plan titles for enrichment
    // Disambiguate multiple FKs on linked_goal_id using constraint name
    .select('*, goals!fk_linked_goal(title, color_key), plans(title)')
    .eq('user_id', userId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });
  if (committedOnly) fullQuery = fullQuery.eq('user_committed', true);
  if (signal) fullQuery = fullQuery.abortSignal(signal);
  const { data, error } = await fullQuery;

  if (error) {
    if (isAbortError(error)) {
      logger.debug('⏹️ taskService.performFullFetch: Aborted by signal');
      throw error;
    }
    logger.error('❌ taskService.performFullFetch: Supabase error:', error);
    throw error;
  }

  logger.info(
    '✅ taskService.performFullFetch: Fetched from Supabase:',
    data?.length || 0,
    'tasks',
  );

  // ESTABLISH CACHE BASELINE
  // =======================
  // Normalize and store ALL tasks in permanent cache with current timestamp
  // This timestamp becomes the reference point for future incremental syncs
  // Future syncs will only fetch tasks newer/updated than this timestamp
  const syncTimestamp = new Date().toISOString();
  const normalized = Array.isArray(data) ? data.map(normalizeTask) : [];
  // Zod validation (non-blocking): log issues but cache original normalized rows
  const validated = normalized.map((row) => {
    const res = TaskSchema.safeParse(row);
    if (!res.success) {
      logger.warn('taskService.performFullFetch: Task validation failed (non-blocking)', {
        id: /** @type {any} */ (row)?.id,
        issues: res.error.issues?.slice(0, 3),
      });
      return row;
    }
    return res.data;
  });
  cacheManager.mergeIncremental(cacheKey, validated, userId, syncTimestamp);

  logger.debug(
    '✅ taskService.performFullFetch: Cached',
    normalized.length,
    'tasks with sync timestamp:',
    syncTimestamp,
  );
  return /** @type {Task[]} */ (normalized);
}

/**
 * Create a new task and add it to permanent cache
 * @param {string} userId - User ID
 * @param {object} taskData - Task data to create
 * @returns {Promise<Task>} Created task
 */
export async function createTask(userId, taskData) {
  logger.info('📋 taskService.createTask: Creating new task for user:', userId);

  // Ensure tasks created from the UI are tagged as user-created by default
  // Ensure a default due date of TODAY if none was provided
  // We set it at the service layer so all callers get a consistent default.
  // Use a broad record type so TS tooling in JS mode doesn't complain
  // when we add a default field like due_date.
  const baseData = /** @type {Record<string, any>} */ ({ ...taskData });
  // Determine if a due date was effectively provided (non-empty)
  const providedDue =
    (typeof baseData.due_date === 'string' ? baseData.due_date.trim() : baseData.due_date) ??
    (typeof baseData.deadline === 'string' ? baseData.deadline.trim() : baseData.deadline);
  if (!providedDue) {
    // Format: YYYY-MM-DD using local time (matches UI expectations)
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    baseData.due_date = `${y}-${m}-${d}`;
  }

  const insertPayload = {
    ...dbFormatTask(baseData),
    user_id: userId,
    created_by: Object.prototype.hasOwnProperty.call(taskData || {}, 'created_by')
      ? /** @type {any} */ (taskData).created_by
      : 'user',
  };

  const { data, error } = await supabase
    .from('tasks')
    // Map UI aliases (title, due_date, completed) back to DB fields
    .insert([insertPayload])
    // Return with related titles so cache is enriched immediately
    // Disambiguate on linked_goal_id
    .select('*, goals!fk_linked_goal(title), plans(title)')
    .single();

  if (error) {
    logger.error('❌ taskService.createTask: Supabase error:', error);
    throw error;
  }

  logger.info('✅ taskService.createTask: Created task:', data.id);

  // ADD TO PERMANENT CACHE
  // =====================
  // Normalize and add new task directly to cache instead of clearing it
  // This maintains instant loading while keeping data fresh
  const cacheKey = 'tasks';
  const syncTimestamp = new Date().toISOString();
  const normalized = /** @type {Task} */ (normalizeTask(data));
  {
    const res = TaskSchema.safeParse(normalized);
    if (!res.success) {
      logger.warn('taskService.createTask: Task validation failed (non-blocking)', {
        id: /** @type {any} */ (normalized)?.id,
        issues: res.error.issues?.slice(0, 3),
      });
      cacheManager.mergeIncremental(cacheKey, [normalized], userId, syncTimestamp);
    } else {
      cacheManager.mergeIncremental(cacheKey, [res.data], userId, syncTimestamp);
    }
  }

  logger.debug('✅ taskService.createTask: Added new task to permanent cache');

  // Best-effort analytics: record task_created event
  try {
    /** @type {Record<string, any>} */
    const client = {
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      locale:
        (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
        'en-US',
      app_session_id: getAppSessionId(),
      app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
    };
    if (client.app_version === undefined) delete client.app_version;
    /** @type {Record<string, any>} */
    const meta = {
      entity_type: 'task',
      entity_id: normalized.id,
      title: normalized.title,
      due_date: normalized.due_date,
      route: typeof window !== 'undefined' ? window.location?.pathname : undefined,
    };
    if (meta.route === undefined) delete meta.route;
    const { error } = await supabase
      .from('user_activity_events')
      .insert([
        {
          user_id: userId,
          event_type: 'task_created',
          session_id: getUuidSessionId(),
          client,
          meta,
        },
      ])
      .select('id')
      .single();
    if (error) {
      console.warn('[DEBUG] task_created insert failed:', {
        error,
        details: error.details,
        hint: error.hint,
        message: error.message,
      });
    }
  } catch (err) {
    console.warn('[DEBUG] task_created exception:', err);
  }
  return normalized;
}

/**
 * Update an existing task and refresh it in permanent cache
 * @param {string} userId - User ID
 * @param {string} taskId - Task ID to update
 * @param {object} updates - Updates to apply
 * @returns {Promise<Task>} Updated task
 */
export async function updateTask(userId, taskId, updates) {
  logger.info('📋 taskService.updateTask: Updating task:', taskId, 'for user:', userId);
  logger.info('📋 taskService.updateTask: Raw updates:', JSON.stringify(updates));
  const formatted = dbFormatTask(updates);
  logger.info('📋 taskService.updateTask: Formatted payload:', JSON.stringify(formatted));

  const { data, error } = await supabase
    .from('tasks')
    // Map UI aliases (title, due_date, completed) back to DB fields
    .update(formatted)
    .eq('id', taskId)
    .eq('user_id', userId)
    // Return with related titles so cache is enriched immediately
    // Disambiguate on linked_goal_id
    .select('*, goals!fk_linked_goal(title, color_key), plans!fk_plan(title)')
    .single();

  if (error) {
    logger.error('❌ taskService.updateTask: Supabase error:', error);
    throw error;
  }

  logger.info('✅ taskService.updateTask: Updated task:', data.id);

  // UPDATE IN PERMANENT CACHE
  // ========================
  // Normalize and merge updated task into cache (replaces existing by ID)
  // This ensures cache reflects the latest changes immediately
  // Critical for tasks due to frequent status updates
  const cacheKey = 'tasks';
  const syncTimestamp = new Date().toISOString();
  const normalized = /** @type {Task} */ (normalizeTask(data));
  cacheManager.mergeIncremental(cacheKey, [normalized], userId, syncTimestamp);

  logger.debug('✅ taskService.updateTask: Updated task in permanent cache');

  // Best-effort analytics: record task_updated and completion toggle if present
  try {
    /** @type {Record<string, any>} */
    const client = {
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      locale:
        (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
        'en-US',
      app_session_id: getAppSessionId(),
      app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
    };
    if (client.app_version === undefined) delete client.app_version;
    /** @type {Record<string, any>} */
    const baseMeta = {
      entity_type: 'task',
      entity_id: normalized.id,
      title: normalized.title,
      due_date: normalized.due_date,
      route: typeof window !== 'undefined' ? window.location?.pathname : undefined,
    };
    if (baseMeta.route === undefined) delete baseMeta.route;

    // Always record task_updated
    const { error: updateError } = await supabase
      .from('user_activity_events')
      .insert([
        {
          user_id: userId,
          event_type: 'task_updated',
          session_id: getUuidSessionId(),
          client,
          meta: baseMeta,
        },
      ])
      .select('id')
      .single();
    if (updateError) {
      console.warn('[DEBUG] task_updated insert failed:', {
        error: updateError,
        details: updateError.details,
        hint: updateError.hint,
        message: updateError.message,
      });
    }

    // If completion field explicitly provided, record completion/uncompletion
    const hasCompletedField =
      Object.prototype.hasOwnProperty.call(updates || {}, 'completed') ||
      Object.prototype.hasOwnProperty.call(updates || {}, 'done');
    if (hasCompletedField) {
      const completedVal = Object.prototype.hasOwnProperty.call(updates || {}, 'completed')
        ? /** @type {any} */ (updates).completed
        : /** @type {any} */ (updates).done;
      const evt = completedVal ? 'task_completed' : 'task_uncompleted';
      /** @type {Record<string, any>} */
      const meta = { ...baseMeta, completed: Boolean(completedVal) };
      const { error: completeError } = await supabase
        .from('user_activity_events')
        .insert([
          { user_id: userId, event_type: evt, session_id: getUuidSessionId(), client, meta },
        ])
        .select('id')
        .single();
      if (completeError) {
        console.warn('[DEBUG] task_completed/uncompleted insert failed:', {
          error: completeError,
          details: completeError.details,
          hint: completeError.hint,
          message: completeError.message,
        });
      }
    }
  } catch (_) {
    // ignore analytics errors
  }
  return normalized;
}

/**
 * Delete a task and remove it from permanent cache
 * @param {string} userId - User ID
 * @param {string} taskId - Task ID to delete
 * @returns {Promise<boolean>} Success status
 */
export async function deleteTask(userId, taskId) {
  logger.info('📋 taskService.deleteTask: Deleting task:', taskId, 'for user:', userId);

  // Soft delete: set deleted_at to current timestamp
  const deletedAt = new Date().toISOString();
  const { data: updated, error } = await supabase
    .from('tasks')
    .update({ deleted_at: deletedAt })
    .eq('id', taskId)
    .eq('user_id', userId)
    .select('id, deleted_at');

  if (error) {
    logger.error('❌ taskService.deleteTask: Supabase error:', error);
    throw error;
  }
  if (!updated || updated.length === 0) {
    logger.warn(
      '⚠️ taskService.deleteTask: No rows updated. Possible user_id mismatch or RLS policy.',
      { userId, taskId },
    );
    throw new Error('Delete failed: no matching task found or permission denied');
  }
  logger.info('✅ taskService.deleteTask: Deleted task from database', updated);

  logger.info('✅ taskService.deleteTask: Deleted task from database');

  // REMOVE FROM PERMANENT CACHE (item-level)
  // =======================================
  // Filter the deleted task out of the cached array to avoid clearing the whole list.
  const cacheKey = 'tasks';
  try {
    const prev = /** @type {Task[]|null} */ (cacheManager.get(cacheKey, userId));
    const next = Array.isArray(prev) ? prev.filter((t) => String(t?.id) !== String(taskId)) : [];
    cacheManager.set(cacheKey, next, userId);
    logger.debug('✅ taskService.deleteTask: Removed task from cache (item-level)');
  } catch (e) {
    logger.warn('taskService.deleteTask: Failed to remove from cache (non-blocking):', e);
  }

  // Best-effort analytics: record task_deleted
  try {
    /** @type {Record<string, any>} */
    const client = {
      platform: typeof navigator !== 'undefined' ? navigator.platform || 'web' : 'web',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      locale:
        (typeof navigator !== 'undefined' && (navigator.language || navigator.languages?.[0])) ||
        'en-US',
      app_session_id: getAppSessionId(),
      app_version: process.env.REACT_APP_VERSION || process.env.REACT_APP_APP_VERSION || undefined,
    };
    if (client.app_version === undefined) delete client.app_version;
    /** @type {Record<string, any>} */
    const meta = {
      entity_type: 'task',
      entity_id: taskId,
      route: typeof window !== 'undefined' ? window.location?.pathname : undefined,
    };
    if (meta.route === undefined) delete meta.route;
    const { error } = await supabase
      .from('user_activity_events')
      .insert([
        {
          user_id: userId,
          event_type: 'task_deleted',
          session_id: getUuidSessionId(),
          client,
          meta,
        },
      ])
      .select('id')
      .single();
    if (error) {
      console.warn('[DEBUG] task_deleted insert failed:', {
        error,
        details: error.details,
        hint: error.hint,
        message: error.message,
      });
    }
  } catch (err) {
    console.warn('[DEBUG] task_deleted exception:', err);
  }
  return true;
}
